import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { Row, Input, Button, Dropdown, Table } from "antd";
import type { MenuProps, TableColumnsType } from "antd";
import { useDebounce } from "@react-hook/debounce";
import { ApolloError } from "@apollo/client";
import { HiSearch } from "react-icons/hi";

import { useGetShopProductByPropertyIdsQuery, type GetShopProductByPropertyIdsQueryHookResult as ShopProductsQueryType } from "graphql/queries/get-shop-products-by-property-ids.generated";
import { useGetFilteredShopProductsQuery } from "graphql/queries/get-filtered-shop-products.generated";
import {
  Shop_Products,
  User_Properties,
  Properties,
} from "graphql/generated/graphql";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useCartStatus } from "state/reactive-vars/cart-status-var";
import { useOrderFiltersStore } from "state/zustand-stores/order-filters-store";


import routeNames from "constants/route-names";
import ProductCard from "components/product-card/product-card";
import { createLabelForFilters } from "utils/helpers";
import ScrollToTopButton from "components/scroll-to-top-button/scroll-to-top-button";
import { SEARCH_DEBOUNCE_DELAY_MS } from "constants/constants";
import ViewFilter from "components/view-filter/view-filter";

interface ShopProductProperties {
  properties: Array<Properties>;
}

type ShopProductsDataType = Shop_Products & ShopProductProperties;

const Order: React.FC = () => {
  const navigate = useNavigate();
  const { loggedUserValue } = useLoggedUser();
  const { cartStatusValue } = useCartStatus();
  const [userPropertiesIds, setUserPropertiesIds] = useState<Array<string>>([]);
  const [productCategories, setProductCategories] = useState<Map<string, string>>();

  const [propertiesDropdownOpen, setPropertiesDropdownOpen] =
    useState<boolean>(false);

  const [categoriesDropdownOpen, setCategoriesDropdownOpen] =
    useState<boolean>(false);

  const [dataSource, setDataSource] = useState<Array<ShopProductsDataType>>([]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const gridView = useOrderFiltersStore((state) => state.filters.gridView);

  const selectedPropertiesFilter = useOrderFiltersStore(
    (state) => state.filters.selectedPropertiesFilter
  );
  const selectedCategoriesFilter = useOrderFiltersStore(
    (state) => state.filters.selectedCategoriesFilter
  );

  const setGridView = useOrderFiltersStore((state) => state.setGridView);
  const setPropertyFilters = useOrderFiltersStore(
    (state) => state.setPropertyFilters
  );
  const setCategoryFilters = useOrderFiltersStore(
    (state) => state.setCategoryFilters
  );

  const searchTerm = useOrderFiltersStore(
    (state) => state.filters.searchOrderTerm
  );

  const setSearchTerm = useOrderFiltersStore((state) => state.setSearchTerm);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(
    searchTerm,
    SEARCH_DEBOUNCE_DELAY_MS
  );

  useEffect(() => {
    setDebouncedSearchTerm(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (loggedUserValue?.user_properties?.length) {
      const loggedUserPropertiesIds = loggedUserValue.user_properties.map(
        (userprop: User_Properties) => userprop.property_id
      );

      setUserPropertiesIds(loggedUserPropertiesIds);
    }
  }, [loggedUserValue]);

  const productsFilter = useMemo(() => {
    const whereClause: any = {};

    if (selectedPropertiesFilter.length) {
      whereClause.property_id = { _in: selectedPropertiesFilter };
    }

    if (selectedCategoriesFilter.length) {
      whereClause.product = {
        _and: [{ type: { _in: selectedCategoriesFilter } }],
      };
    } else {
      whereClause.product = {};
    }

    if (debouncedSearchTerm) {
      whereClause._or = [
        { product: { name: { _ilike: `%${debouncedSearchTerm}%` } } },
        { product: { make: { _ilike: `%${debouncedSearchTerm}%` } } },
        { product: { model: { _ilike: `%${debouncedSearchTerm}%` } } },
        {
          product: {
            product_attachments: {
              transcription: { _ilike: `%${debouncedSearchTerm}%` },
            },
          },
        },
      ];
    }

    return whereClause;
  }, [
    loggedUserValue,
    selectedPropertiesFilter,
    selectedCategoriesFilter,
    debouncedSearchTerm,
  ]);

  const handleOnClickProduct = useCallback((product_id) => {
    const path = `${routeNames.SHOP_PRODUCTS_DETAILS.replace(
      ":productId",
      product_id
    )}`;

    navigate(path);
  }, []);

  const handleSearchTermChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Directly update the search term in Zustand
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  const handleOnSelectPropertiesFilter = useCallback(
    (args) => {
      const { selectedKeys } = args;

      if (selectedKeys.includes("property-dropdown-option-all")) {
        setPropertyFilters([]);
        return;
      }

      setPropertyFilters(selectedKeys);
    },
    [setPropertyFilters]
  );

  const handleOnSelectCategoriesFilter = useCallback(
    (args) => {
      const { selectedKeys } = args;

      if (selectedKeys.includes("category-dropdown-option-all")) {
        setCategoryFilters([]);
        return;
      }

      setCategoryFilters(selectedKeys);
    },
    [setCategoryFilters]
  );

  const handleOnCompleteGetShopProductByPropertyIds = useCallback(
    (data: ShopProductsQueryType['data']) => {
      if (data?.shop_products) {
        const productUniqueTypes = data.shop_products.filter(
          (element, index, self) =>
            index ===
            self.findIndex((t) => t.product?.product_category.label === element.product?.product_category.label)
        );

        const productCategoryMap = new Map();

        productUniqueTypes.forEach((sp) => {
          productCategoryMap.set(sp.product?.product_category.name, sp.product?.product_category.label)
        })
        setProductCategories(productCategoryMap);
      }
    },
    []
  );

  const handleOnErrorGetShopProductByPropertyIds = useCallback(
    (error: ApolloError) => {
      console.log("Error GetShopProduct By PropertyIds error: ", error);
    },
    []
  );

  const {
    data: shopProductsData,
    loading,
    error,
  } = useGetShopProductByPropertyIdsQuery({
    skip: !userPropertiesIds.length,
    variables: {
      property_ids: userPropertiesIds,
    },
    errorPolicy: "none",
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorGetShopProductByPropertyIds,
    onCompleted: handleOnCompleteGetShopProductByPropertyIds,
  });

  const handleOnErrorGetFilteredShopProducts = useCallback(
    (error: ApolloError) => {
      console.log("useGetFilteredShopProductsQuery Error: ", error);
    },
    []
  );

  const handleOnCompleteGetFilteredShopProducts = useCallback((data) => {
    const { shop_products } = data || {};

    const productsData = shop_products.reduce((accumulator, currentValue) => {
      const existingProductIndex = accumulator.findIndex(
        (element) => element.product_id === currentValue.product_id
      );

      if (existingProductIndex != -1) {
        accumulator[existingProductIndex].properties.push(
          currentValue.property
        );
      } else {
        const productWithProperties = Object.assign({}, currentValue, {
          properties: [currentValue.property],
        });
        accumulator.push(productWithProperties);
      }

      return accumulator;
    }, []);

    setDataSource(productsData);
  }, []);

  useGetFilteredShopProductsQuery({
    variables: {
      where: productsFilter,
    },
    errorPolicy: "none",
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorGetFilteredShopProducts,
    onCompleted: handleOnCompleteGetFilteredShopProducts,
  });

  const tableColumns: TableColumnsType = useMemo(() => {
    const fixedColumns = [
      {
        title: "DEVICE",
        dataIndex: "product",
        render: (item) => {
          return item.name;
        },
        key: `PRODUCT`,
      },
      {
        title: "TYPE",
        dataIndex: "product",
        render: (item) => {
          return item.product_category.label;
        },
        key: `TYPE`,
      },
      {
        title: "PROPERTIES",
        dataIndex: "properties",
        render: (field, record, index) => {
          return (
            <div className="shop-product-property-container">
              {field.map((element) => (
                <div
                  className="shop-product-property"
                  key={`${record.id}-${element.id}`}
                >
                  {element.name}
                </div>
              ))}
            </div>
          );
        },
        key: `PROPERTIES`,
      },
    ];

    const priceColumns = [
      {
        title: "RETAIL PRICE",
        dataIndex: "product",
        render: (item) => {
          return item.retail_price;
        },
        key: `RETAIL PRICE`,
      },
      {
        title: "EXTENDED PRICE",
        dataIndex: "product",
        render: (item) => {
          return item.extended_price;
        },
        key: `EXTENDED PRICE`,
      },
    ];

    if (loggedUserValue.organization?.display_price) {
      const columns = fixedColumns.concat(priceColumns);
      return columns;
    } else {
      return fixedColumns;
    }
  }, [loggedUserValue]);

  const [propertiesOptions, propertyDropdownLabel = "Property"] =
    useMemo(() => {
      const { shop_products } = shopProductsData || {};
      if (shop_products?.length) {
        const productUniqueProperties = shop_products.filter(
          (element, index, self) =>
            index ===
            self.findIndex((t) => t.property_id === element.property_id)
        );

        let selectedProperty;
        const productPropertiesValues = productUniqueProperties.map((p) => {
          if (
            !selectedProperty &&
            p.property_id === selectedPropertiesFilter[0]
          ) {
            selectedProperty = p.property;
          }
          return p.property;
        });

        if (productPropertiesValues?.length) {
          const productProperties: MenuProps["items"] =
            productPropertiesValues.reduce(
              (options, property, currentindex, array) => {
                const optionId = `property-dropdown-option-${currentindex + 1
                  }-${property?.id}`;

                const isSelected = selectedPropertiesFilter.includes(
                  property?.id as never
                );

                const newOption = {
                  key: `${property?.id}`,
                  icon: (
                    <div>
                      <input
                        className="custom-checkbox"
                        id={optionId}
                        type="checkbox"
                        onClick={(e) => e.stopPropagation()}
                        checked={isSelected}
                      />
                      <label
                        htmlFor={optionId}
                        className="custom-checkbox-label"
                      >
                        {property?.name}
                      </label>
                    </div>
                  ),
                };

                options.push(newOption);

                return options;
              },
              [
                {
                  key: "property-dropdown-option-all",
                  icon: (
                    <div>
                      <input
                        className="custom-checkbox"
                        id="property-dropdown-option-all"
                        type="checkbox"
                        onClick={(e) => e.stopPropagation()}
                        checked={selectedPropertiesFilter.length === 0}
                      />
                      <label
                        htmlFor="property-dropdown-option-all"
                        className="custom-checkbox-label"
                      >
                        All properties
                      </label>
                    </div>
                  ),
                },
              ]
            );

          return [
            productProperties,
            createLabelForFilters(
              "Property",
              selectedPropertiesFilter,
              selectedProperty?.name
            ),
          ];
        }
      }

      return [[], "Property"];
    }, [shopProductsData, selectedPropertiesFilter]);

  const [categoriesOptions, categoryDropdownLabel = "Category"] =
    useMemo(() => {
      if (!!productCategories?.size) {
        const selectedValues = selectedCategoriesFilter.map((v) => productCategories.get(v));
        const productCategoryOptions: MenuProps["items"] = [
          {
            key: "category-dropdown-option-all",
            icon: (
              <div>
                <input
                  className="custom-checkbox"
                  id="category-dropdown-option-all"
                  type="checkbox"
                  onClick={(e) => e.stopPropagation()}
                />
                <label
                  htmlFor="category-dropdown-option-all"
                  className="custom-checkbox-label"
                >
                  All Categories
                </label>
              </div>
            ),
          },
        ];

        productCategories.forEach(
          (value, key) => {
            const optionId = `category-dropdown-option-${key}`;
            const isSelected = selectedCategoriesFilter?.includes(key);

            const newOption = {
              key: key,
              icon: (
                <div>
                  <input
                    className="custom-checkbox"
                    id={optionId}
                    type="checkbox"
                    onClick={(e) => e.stopPropagation()}
                    checked={isSelected}
                  />
                  <label
                    htmlFor={optionId}
                    className="custom-checkbox-label"
                  >
                    {value}
                  </label>
                </div>
              ),
            };
            productCategoryOptions.push(newOption);
          },
        );

        return [
          productCategoryOptions,
          createLabelForFilters("Category", selectedValues),
        ];
      }

      return [[], "Category"];
    }, [productCategories, selectedCategoriesFilter]);

  return (
    <div className="products-screen-container">
      <span className="page-title">{"Order"}</span>
      <div className="products-content-container" ref={scrollContainerRef}>
        <div className="products-filter-group">
          <div className="search-products-row">
            <div className="products-search-container">
              <Input
                className="product-search-input-wrapper"
                name="searchProduct"
                placeholder="Search..."
                classNames={{ input: "product-search-input" }}
                value={searchTerm} // Use Zustand search term
                autoComplete="off"
                onChange={handleSearchTermChange}
                prefix={<HiSearch color={"#67748E"} size={"16px"} />}
                allowClear
              />
            </div>
            <ViewFilter gridView={gridView} setGridView={setGridView} />
          </div>
          {/* <FiPlusCircle
              size={24}
              className="clickable"
              color={"#67748e"}
              onClick={() => navigate(routeNames.ADMIN_DEVICES_ADD)}
            />
            <img
            src={
              process.env.PUBLIC_URL +
              "/assets/images/icons/arrow-circle-download.svg"
            }
            className="clickable"
          /> */}
          <div className="products-filters-row">
            <Dropdown
              menu={{
                items: propertiesOptions,
                selectable: true,
                multiple: true,
                onSelect: (args) => handleOnSelectPropertiesFilter(args),
                onDeselect: (args) => handleOnSelectPropertiesFilter(args),
                selectedKeys: selectedPropertiesFilter,
              }}
              arrow
              className="products-dropdown"
              overlayClassName="products-dropdown-overlay"
              trigger={["click"]}
              open={propertiesDropdownOpen}
              onOpenChange={(
                open: boolean,
                info: { source: "trigger" | "menu" }
              ) => {
                if (info.source === "trigger" && open === false) {
                  setPropertiesDropdownOpen(false);
                }
              }}
              disabled={propertiesOptions.length === 0}
            >
              <Button
                onClick={() => {
                  setPropertiesDropdownOpen((previousValue) => !previousValue);
                }}
              >
                {propertyDropdownLabel}
                <img
                  src={
                    process.env.PUBLIC_URL +
                    "/assets/images/icons/black-chevron-down.svg"
                  }
                  style={{ width: "1em", height: "1em" }}
                  title={propertyDropdownLabel}
                  className="chevron-button"
                />
              </Button>
            </Dropdown>
            <Dropdown
              menu={{
                items: categoriesOptions,
                selectable: true,
                multiple: true,
                onSelect: (args) => handleOnSelectCategoriesFilter(args),
                onDeselect: (args) => handleOnSelectCategoriesFilter(args),
                selectedKeys: selectedCategoriesFilter,
                defaultSelectedKeys: selectedCategoriesFilter,
              }}
              arrow
              className="products-dropdown"
              trigger={["click"]}
              open={categoriesDropdownOpen}
              onOpenChange={(
                open: boolean,
                info: { source: "trigger" | "menu" }
              ) => {
                if (info.source === "trigger" && open === false) {
                  setCategoriesDropdownOpen(false);
                }
              }}
              disabled={categoriesOptions.length === 0}
            >
              <Button
                onClick={() => {
                  setCategoriesDropdownOpen((previousValue) => !previousValue);
                }}
              >
                {categoryDropdownLabel}
                <img
                  src={
                    process.env.PUBLIC_URL +
                    "/assets/images/icons/black-chevron-down.svg"
                  }
                  style={{ width: "1em", height: "1em" }}
                  title={categoryDropdownLabel}
                  className="chevron-button"
                />
              </Button>
            </Dropdown>
            <ViewFilter gridView={gridView} setGridView={setGridView} />
          </div>
        </div>
        <div className="products-table">
          {gridView ? (
            <Row className="products-grid-container" align="stretch">
              {dataSource.map((element) => {
                const { product, properties, product_id } = element;

                const productQuantity = cartStatusValue.items.reduce(
                  (sum, item) => {
                    if (item.product.id === product?.id) {
                      sum = sum + item.quantity;
                      return sum;
                    }

                    return sum;
                  },
                  0
                );

                if (product) {
                  return (
                    <ProductCard
                      key={`shop-product-${element.id}`}
                      product={product}
                      entityId={product_id}
                      properties={properties}
                      productQuantity={productQuantity}
                      handleOnClickProduct={handleOnClickProduct}
                      shopProduct
                    />
                  );
                } else {
                  return null;
                }
              })}
            </Row>
          ) : (
            <div className="products-table-container">
              <Table
                dataSource={dataSource}
                columns={tableColumns}
                rowKey={(record) => record.id}
                onRow={(record, rowIndex) => {
                  return {
                    onClick: (event) => {
                      handleOnClickProduct(record.product_id);
                    },
                  };
                }}
              />
            </div>
          )}
        </div>
        <ScrollToTopButton parentRef={scrollContainerRef} />
      </div>
    </div>
  );
};

export default Order;
